/**
 * La tarjeta que le dice al cliente qué sistema necesitaría, mientras escribe.
 *
 * Es lo único de toda la página que enseña números del cálculo, y por eso hay una regla
 * que no se toca: **aquí no entra ni una cantidad de dinero**. Tamaño sí —kWp, paneles,
 * energía generada—; precio no. Eso lo dice un asesor después de mirar el caso, y es la
 * regla de negocio del proyecto, no un pendiente.
 *
 * El servidor tampoco los manda: `test_recomendacion.py` en el motor falla si la
 * respuesta gana un campo de dinero. Esta pantalla es la última barrera, no la única.
 */

import type { Recomendacion as Datos } from '../api.js'

interface Props {
  datos: Datos | null
  cargando: boolean
}

export function Recomendacion({ datos, cargando }: Props) {
  if (cargando) {
    return (
      <div className="recomendacion cargando" aria-live="polite">
        Calculando qué sistema necesitas…
      </div>
    )
  }

  if (!datos) return null

  if (datos.fuera_de_catalogo) {
    return (
      <div className="recomendacion grande" aria-live="polite">
        <h3>Tu consumo es grande</h3>
        <p>
          Necesitarías del orden de <strong>{formatear(datos.potencia_kwp)} kWp</strong>. Un
          sistema de ese tamaño no se diseña con un formulario: hace falta un estudio en
          sitio y trámite ante ARCONEL.
        </p>
        <p className="recomendacion-nota">
          Envía tus datos igual — es justo el tipo de proyecto que trabajamos a medida.
        </p>
      </div>
    )
  }

  return (
    <div className="recomendacion" aria-live="polite">
      <h3>Para cubrir todo tu consumo necesitarías</h3>

      <div className="recomendacion-cifras">
        <div>
          <strong>{formatear(datos.potencia_kwp)}</strong>
          <span>kWp de potencia</span>
        </div>
        <div>
          <strong>{datos.paneles}</strong>
          <span>paneles de {datos.potencia_panel_w} W</span>
        </div>
        <div>
          <strong>
            {datos.inversores > 1 && `${datos.inversores} × `}
            {formatear(datos.potencia_inversor_kw)}
          </strong>
          <span>kW de inversor</span>
        </div>
      </div>

      <p className="recomendacion-genera">
        Generaría unos <strong>{datos.generacion_mensual_kwh.toLocaleString('es-EC')} kWh
        al mes</strong>, alrededor del {datos.cobertura_pct}% de lo que consumes.
      </p>

      {/* Se oculta si el catálogo no tiene las medidas del panel: antes que dar un
          número inventado, que el cliente va a ir a medir a su techo, mejor callarlo. */}
      {datos.area_techo_m2 > 0 && (
        <p className="recomendacion-techo">
          Ocuparían alrededor de <strong>{formatear(datos.area_techo_m2)} m² de techo</strong>
          <span> ({formatear(datos.area_paneles_m2)} m² de paneles, más el paso entre filas)</span>
        </p>
      )}

      <p className="recomendacion-nota">
        Es una estimación con la radiación solar de tu zona. Tu asesor la ajusta a tu techo
        —orientación, sombras, espacio disponible— y te explica las opciones con y sin
        batería.
      </p>
    </div>
  )
}

/** Sin decimales cuando no hacen falta: "8 kWp" se lee mejor que "8,00 kWp". */
function formatear(valor: number): string {
  return valor.toLocaleString('es-EC', { maximumFractionDigits: 1 })
}
